/**
 * Variant Gallery Swap for USP theme (Prestige-based)
 *
 * Mechanism: All variant gallery images (from custom.gallery_images metafield)
 * and the native product media are pre-rendered in the gallery on page load.
 * Non-active variant images and native media (when an overriding variant is
 * active) are marked with the `hidden` attribute.
 *
 * On variant change we just toggle which elements are hidden — no DOM injection,
 * no carousel re-init. Prestige's <scroll-carousel> handles `hidden` items
 * gracefully because that's the same mechanism used for filtered_indexes.
 */
(function () {
  'use strict';

  const VARIANT_INPUT_SELECTOR = 'input[name="id"]';
  const GALLERY_SELECTOR = 'product-gallery';

  /**
   * Apply visibility rules for a given variant on a single gallery instance.
   */
  function applyVariantToGallery(gallery, variantId) {
    const variantsWithCustom = (gallery.dataset.variantsWithCustomGallery || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const hasCustomGallery = variantsWithCustom.includes(String(variantId));

    // Native product media (and their thumbnails/dots) — visible only when
    // the current variant does NOT have a custom gallery.
    const nativeItems = gallery.querySelectorAll('[data-native-media="true"]');
    nativeItems.forEach((el) => {
      // Respect Prestige's native variant-image filtering (filtered_indexes).
      // If the element was hidden by Liquid AND it's a native-media on the
      // carousel side, we let our JS only manage the "hide because variant
      // overrides" case — but we still need to re-show items that were
      // previously hidden by us.
      // Strategy: maintain a data flag `data-vg-hidden` for items we hid.
      if (hasCustomGallery) {
        if (!el.hasAttribute('hidden')) {
          el.setAttribute('hidden', '');
          el.setAttribute('data-vg-hidden', 'true');
        }
      } else {
        if (el.getAttribute('data-vg-hidden') === 'true') {
          el.removeAttribute('hidden');
          el.removeAttribute('data-vg-hidden');
        }
      }
    });

    // Variant-specific images: visible only when their variant ID matches.
    const variantImages = gallery.querySelectorAll('[data-variant-image="true"],[data-variant-thumbnail="true"],[data-variant-dot="true"]');
    variantImages.forEach((el) => {
      if (el.dataset.variantId === String(variantId)) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });

    gallery.dataset.currentVariantId = String(variantId);

    // Tell the scroll-carousel its content changed so it can re-measure /
    // re-scroll to the start. Prestige listens to a few events; we dispatch
    // a generic one and try to call public methods if available.
    const carousel = gallery.querySelector('scroll-carousel');
    if (carousel) {
      // Scroll back to the first visible slide.
      const firstVisible = carousel.querySelector('.product-gallery__media:not([hidden])');
      if (firstVisible && typeof firstVisible.scrollIntoView === 'function') {
        try {
          firstVisible.scrollIntoView({ behavior: 'instant', inline: 'start', block: 'nearest' });
        } catch (e) {
          // Older browsers may not support 'instant'; fall back silently.
          firstVisible.scrollIntoView({ inline: 'start', block: 'nearest' });
        }
      }

      // Dispatch event so any listeners can react.
      carousel.dispatchEvent(new CustomEvent('carousel:updated', { bubbles: true }));
    }

    // Update aria-current on thumbnails — first visible thumbnail becomes current.
    const thumbList = gallery.querySelector('product-gallery-navigation');
    if (thumbList) {
      const thumbs = thumbList.querySelectorAll('.product-gallery__thumbnail');
      let first = true;
      thumbs.forEach((thumb) => {
        if (!thumb.hasAttribute('hidden') && first) {
          thumb.setAttribute('aria-current', 'true');
          first = false;
        } else {
          thumb.setAttribute('aria-current', 'false');
        }
      });
    }
  }

  /**
   * Run for all product galleries on the page.
   */
  function applyVariant(variantId) {
    if (!variantId) return;
    const galleries = document.querySelectorAll(GALLERY_SELECTOR);
    galleries.forEach((g) => applyVariantToGallery(g, variantId));
  }

  /**
   * Find the variant id from the page — prefer the closest product form's
   * hidden id input.
   */
  function getCurrentVariantId(scope) {
    const root = scope || document;
    const input = root.querySelector(VARIANT_INPUT_SELECTOR);
    return input ? input.value : null;
  }

  function init() {
    // Initial render — make sure visibility matches the selected variant.
    const initialId = getCurrentVariantId();
    if (initialId) applyVariant(initialId);

    // Prestige dispatches 'variant:change' on the product form when a
    // variant is selected. The event detail typically contains { variant }.
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail && e.detail.variant;
      const id = variant ? variant.id : getCurrentVariantId();
      if (id) applyVariant(id);
    });

    // Fallback: listen to changes on the hidden variant input directly.
    document.querySelectorAll(VARIANT_INPUT_SELECTOR).forEach((input) => {
      input.addEventListener('change', () => applyVariant(input.value));

      // Also catch programmatic .value updates via a MutationObserver on the
      // `value` attribute (some themes set the attribute, not the property).
      const observer = new MutationObserver(() => applyVariant(input.value));
      observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
